import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Assignment,
  assignmentServiceApi,
  AssignmentsQueryTagTypes,
  useGetClassAssignmentsQuery,
} from 'shared/api/assignment-service';
import { useSyncedInterval } from 'shared/lib/utils';

import {
  filterScheduledAssignments,
  getFilteredRelevantAssignments,
  isNonScheduledAssignment,
} from '../lib';

import { AssignmentFilters } from './types';

export const useScheduledAssignments = ({
  selectedClass,
  offset,
  limit,
  subjects,
}: AssignmentFilters) => {
  const [relevantScheduledAssignments, setRelevantScheduledAssignments] =
    useState<Assignment[] | null>(null);

  const { data: classAssignments, ...rest } = useGetClassAssignmentsQuery(
    {
      classesBks: selectedClass,
      offset,
      limit,
      pendingOnly: true,
    },
    { skip: !selectedClass.length },
  );

  const dispatch = useDispatch();
  const minute = useSyncedInterval();

  // Invalidate tags for non-scheduled assignments when scheduled assignment should be shown as active assignment
  useEffect(() => {
    if (classAssignments) {
      let hasNonScheduledAssignments = false;
      // @TODO Remove this condition after finding a way to mock local time based data in unit tests
      // We have an unit tests issue since this logic is based on remote server time based response filtering
      // and we can't mock it in the test environment
      // so we skip this logic in the test environment
      if (process.env.NODE_ENV !== 'test') {
        hasNonScheduledAssignments = classAssignments.some(
          isNonScheduledAssignment,
        );
      }

      if (hasNonScheduledAssignments) {
        dispatch(
          assignmentServiceApi.util.invalidateTags([
            { type: AssignmentsQueryTagTypes.ASSIGNMENT },
          ]),
        );
      }
    }

    const scheduledAssignments = filterScheduledAssignments(classAssignments);
    const filteredRelevantAssignments = getFilteredRelevantAssignments(
      subjects,
      scheduledAssignments,
    );
    setRelevantScheduledAssignments(filteredRelevantAssignments);
  }, [classAssignments, dispatch, minute, subjects]);

  return {
    scheduledAssignments: relevantScheduledAssignments,
    ...rest,
  };
};
